import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import React, { useMemo, useState } from "react";
import { Platform, Pressable, ScrollView, View } from "react-native";
import { useTranslation } from "react-i18next";
import { getStatusIconConfig } from "../../Utils/plannerStepUtils";
import { isElectronRunning } from "../../../App";

const StepDetailPanel = ({
  step,
  onClose,
  currentTheme,
  S3downloadUrlRequest,
  workspaceid,
  getAgent,
  renderMarkdown = true,
}) => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState({
    metadata: false,
    sources: false,
  });

  const statusConfig = getStatusIconConfig(step.status, currentTheme);

  // Resolve agent ID to agent name
  const agentDisplayName = useMemo(() => {
    const assignedTo = step.assignedTo || step.agentId;
    const primaryMessage = step.messages?.[0];

    // The real sub-agent name is carried on the bucketed messages (resolved
    // from the message's agentID column). Prefer it for dynamically-spawned
    // agents whose plan placeholder is "new_agent_*" — otherwise the step
    // detail shows the generic "New Agent" label instead of the actual agent
    // (e.g. "TeslaNewsAgent").
    const messageAgentName =
      primaryMessage?.agentName ||
      step.messages?.find((m) => m?.agentName)?.agentName ||
      null;
    const messageAgentId = primaryMessage?.agentID || null;
    const resolvedFromMessage =
      messageAgentName ||
      (getAgent && messageAgentId ? getAgent(messageAgentId)?.label : null);

    if (!assignedTo) return resolvedFromMessage || null;

    // Handle new_agent* pattern (e.g. new_agent_1, new_agent_2): the plan
    // uses a placeholder id for a dynamically spawned agent, so prefer the
    // real name resolved from the bucketed message before falling back to
    // the generic "New Agent" label.
    if (/^new_agent/i.test(assignedTo)) {
      return resolvedFromMessage || t("planner.steps.newAgent");
    }

    // Check if assignedTo is a UUID (agent ID) - try to resolve it
    if (getAgent && typeof getAgent === "function") {
      const agent = getAgent(assignedTo);
      if (agent?.label) return agent.label;
    }

    // Fall back to assignedTo (which is already the agent name from plan parsing),
    // then to the message-resolved name.
    return assignedTo || resolvedFromMessage;
  }, [step, getAgent]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Get the first message for primary content
  const primaryMessage = step.messages?.[0];
  const metadata = primaryMessage?.metadata || {};
  const sources = metadata?.sources || metadata?.retrieval_sources || [];

  // Helper to parse metadata that may arrive as a JSON string.
  const parseMeta = (value) => {
    if (!value) return {};
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch (e) {
        return {};
      }
    }
    return value;
  };

  // Classify a step message as planner narration vs sub-agent output by the
  // backend's from_planner flag (never by text). Planner narration (thinking,
  // "ready to operate", success/retry analysis) is displayed too — previously
  // it was filtered out, which hid step-scoped narration entirely (it is not
  // shown in the main chat either, since it carries a step_id).
  const isNarrationMessage = (msg) => {
    const msgMetadata = parseMeta(msg.metadata);
    const msgCallbackMetadata = parseMeta(msg.callbackMetadata);
    return (
      msgMetadata?.from_planner === true ||
      msgCallbackMetadata?.from_planner === true
    );
  };

  // All messages attributed to this step, in chronological order.
  const stepMessages = step.messages || [];

  const hasDisplayableText = stepMessages.some(
    (msg) => msg.text && msg.text !== "**...**",
  );

  // Format status label
  const getStatusLabel = (status) => {
    return t(`planner.status.${status}`, { defaultValue: status });
  };

  return (
    <View
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: currentTheme?.theme?.inputBackgroundColor,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: currentTheme?.theme?.border_color,
        zIndex: 10,
      }}
    >
      {/* Header */}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 15,
          borderBottomWidth: 1,
          borderBottomColor: currentTheme?.theme?.border_color,
        }}
      >
        <View style={{ flex: 1 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginBottom: 4,
            }}
          >
            <AppText
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.darkGrey,
              }}
            >
              Step {step.stepIndex + 1}
            </AppText>

            {/* Status badge */}
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: statusConfig.color,
                paddingHorizontal: 6,
                paddingVertical: 2,
                borderRadius: 10,
                marginLeft: 8,
              }}
            >
              <AppIcon
                icon={
                  AppAssets?.icons?.[statusConfig.iconKey] ||
                  AppAssets?.icons?.pending
                }
                size={10}
                color="#FFFFFF"
                wrapperStyle={{ marginRight: 4 }}
              />
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                  color: "#FFFFFF",
                  fontWeight: "500",
                }}
              >
                {getStatusLabel(step.status)}
              </AppText>
            </View>
          </View>

          <AppText
            fontWeight="bold"
            style={{
              fontSize: currentTheme?.theme?.small_font_size,
              color: currentTheme?.theme?.font_color,
            }}
            preventTranslation
          >
            {step.taskName}
          </AppText>
        </View>

        {/* Back button */}
        <Pressable
          onPress={onClose}
          style={({ hovered }) => ({
            padding: 8,
            borderRadius: 6,
            backgroundColor: hovered
              ? currentTheme?.theme?.grey
              : "transparent",
          })}
        >
          <AppIcon
            icon={AppAssets?.icons?.back_icon}
            color={currentTheme?.theme?.darkGrey}
            size={16}
          />
        </Pressable>
      </View>

      {/* Content */}
      <ScrollView
        style={{
          flex: 1,
          padding: 15,
          overflowY: Platform.OS === "web" ? "auto" : "scroll",
        }}
        showsVerticalScrollIndicator={true}
      >
        {/* Agent info */}
        {agentDisplayName && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginBottom: 15,
              paddingBottom: 10,
              borderBottomWidth: 1,
              borderBottomColor: currentTheme?.theme?.border_color,
            }}
          >
            <AppText
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.darkGrey,
              }}
            >
              {t("planner.steps.assignedTo")}{" "}
            </AppText>
            <AppText
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.font_color,
                fontWeight: "500",
              }}
              preventTranslation
            >
              {agentDisplayName}
            </AppText>
          </View>
        )}

        {/* Full step description from plan */}
        {step.fullDescription && (
          <View
            style={{
              backgroundColor: currentTheme?.theme?.softBackgroundColor,
              borderRadius: 8,
              padding: 12,
              marginBottom: 15,
            }}
          >
            <AppText
              fontWeight="500"
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.darkGrey,
                marginBottom: 6,
              }}
            >
              {t("planner.steps.stepDescription")}
            </AppText>
            <AppText
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.font_color,
                lineHeight: 20,
              }}
              preventTranslation
            >
              {step.fullDescription}
            </AppText>
          </View>
        )}

        {/* Step messages in chronological order. Both planner narration
            (metadata.from_planner === true) and sub-agent output are shown;
            narration is rendered in a distinct muted style so it reads as
            orchestrator commentary, while sub-agent output is the primary
            content. Shows an in-progress placeholder only when there is no
            displayable text at all (e.g. mid-stream before any content
            lands), so the panel never collapses to just an artifact image. */}
        {!hasDisplayableText && !step.isFailed ? (
          <View
            style={{
              backgroundColor: currentTheme?.theme?.softBackgroundColor,
              borderRadius: 8,
              padding: 12,
              marginBottom: 15,
              alignItems: "center",
            }}
          >
            <AppText
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.darkGrey,
                fontStyle: "italic",
              }}
            >
              {step.isActive
                ? t("planner.steps.streamingInProgress", {
                    defaultValue: "Agent is working on this step…",
                  })
                : t("planner.steps.noOutputYet", {
                    defaultValue: "No agent output available for this step.",
                  })}
            </AppText>
          </View>
        ) : (
          stepMessages.map((msg, index) => {
            const isNarration = isNarrationMessage(msg);
            if (!msg.text || msg.text === "**...**") return null;
            return (
              <View key={msg.messageId || index} style={{ marginBottom: 15 }}>
                {isNarration ? (
                  <View
                    style={{
                      backgroundColor: currentTheme?.theme?.softBackgroundColor,
                      borderLeftWidth: 3,
                      borderLeftColor: currentTheme?.theme?.primary,
                      borderRadius: 4,
                      padding: 10,
                    }}
                  >
                    <AppText
                      preventTranslation
                      currentTheme={currentTheme}
                      renderMarkdown={renderMarkdown}
                      S3downloadUrlRequest={S3downloadUrlRequest}
                      workspaceid={workspaceid}
                      renderHTMLLinkPreview={true}
                      coder={true}
                      hideReasoning={true}
                      suppressCopyButton={true}
                      suppressTextToSpeech={true}
                      markdownStyles={{
                        backgroundColor: "transparent",
                      }}
                    >
                      {msg.text}
                    </AppText>
                  </View>
                ) : (
                  <View
                    style={{
                      backgroundColor: currentTheme?.theme?.softBackgroundColor,
                      borderRadius: 8,
                      padding: 12,
                    }}
                  >
                    <AppText
                      preventTranslation
                      currentTheme={currentTheme}
                      renderMarkdown={renderMarkdown}
                      S3downloadUrlRequest={S3downloadUrlRequest}
                      workspaceid={workspaceid}
                      showCopyButton={true}
                      renderHTMLLinkPreview={true}
                      coder={true}
                      hideReasoning={true}
                      suppressCopyButton={true}
                      suppressTextToSpeech={true}
                      markdownStyles={{
                        backgroundColor: "transparent",
                      }}
                    >
                      {msg.text}
                    </AppText>
                  </View>
                )}
              </View>
            );
          })
        )}

        {/* Error section */}
        {step.isFailed && step.error && (
          <View
            style={{
              backgroundColor: currentTheme?.theme?.lightGrey,
              borderRadius: 8,
              padding: 12,
              marginBottom: 15,
            }}
          >
            <AppText
              fontWeight="bold"
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.red,
                marginBottom: 4,
              }}
            >
              {t("planner.steps.error")}
            </AppText>
            <AppText
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.red,
              }}
              preventTranslation
            >
              {step.error}
            </AppText>
          </View>
        )}

        {/* Collapsible Metadata Section */}
        {Object.keys(metadata).length > 0 && (
          <CollapsibleSection
            title={t("planner.steps.executionDetails")}
            isExpanded={expandedSections.metadata}
            onToggle={() => toggleSection("metadata")}
            currentTheme={currentTheme}
          >
            <View
              style={{
                backgroundColor: currentTheme?.theme?.softBackgroundColor,
                borderRadius: 6,
                padding: 10,
              }}
            >
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                  color: currentTheme?.theme?.darkGrey,
                  fontFamily: Platform.OS === "web" ? "monospace" : "Courier",
                }}
                preventTranslation
              >
                {JSON.stringify(metadata, null, 2)}
              </AppText>
            </View>
          </CollapsibleSection>
        )}

        {/* Collapsible Sources Section */}
        {sources.length > 0 && (
          <CollapsibleSection
            title={t("planner.steps.sources", { count: sources.length })}
            isExpanded={expandedSections.sources}
            onToggle={() => toggleSection("sources")}
            currentTheme={currentTheme}
          >
            {sources.map((source, index) => (
              <View
                key={index}
                style={{
                  backgroundColor: currentTheme?.theme?.softBackgroundColor,
                  borderRadius: 6,
                  padding: 10,
                  marginBottom: 8,
                }}
              >
                <AppText
                  fontWeight="500"
                  style={{
                    fontSize: currentTheme?.theme?.small_font_size,
                    color: currentTheme?.theme?.font_color,
                    marginBottom: 4,
                  }}
                  preventTranslation
                >
                  {source.title || source.name || `Source ${index + 1}`}
                </AppText>
                {source.text && (
                  <AppText
                    numberOfLines={4}
                    style={{
                      fontSize: currentTheme?.theme?.extra_small_font_size,
                      color: currentTheme?.theme?.darkGrey,
                    }}
                    preventTranslation
                  >
                    {source.text}
                  </AppText>
                )}
              </View>
            ))}
          </CollapsibleSection>
        )}

        {/* Files/Artifacts - Collect from ALL messages in this step */}
        {(() => {
          // Collect all artifacts from all messages in this step
          const allImages = [];
          const allFiles = [];
          const allAudios = [];
          const allVideos = [];
          const allModels = [];
          const seenKeys = new Set();

          // Helper to add artifact if not already seen and not internal
          const addArtifact = (key, targetArray) => {
            // Skip internal step files and already seen keys
            if (key && !seenKeys.has(key) && !key.includes("_internal_step")) {
              seenKeys.add(key);
              targetArray.push(key);
            }
          };

          step.messages?.forEach((msg) => {
            // 1. Message-level artifact arrays
            msg.images?.forEach((img) => addArtifact(img, allImages));
            msg.files?.forEach((file) => addArtifact(file, allFiles));
            msg.audios?.forEach((audio) => addArtifact(audio, allAudios));
            msg.videos?.forEach((video) => addArtifact(video, allVideos));

            // 2. Parse metadata for additional artifacts
            let msgMetadata = msg.metadata || {};
            if (typeof msgMetadata === "string") {
              try {
                msgMetadata = JSON.parse(msgMetadata);
              } catch (e) {
                msgMetadata = {};
              }
            }

            // 2a. exec_result output_files (code execution outputs)
            if (msgMetadata?.exec_result) {
              try {
                const execResult =
                  typeof msgMetadata.exec_result === "string"
                    ? JSON.parse(msgMetadata.exec_result)
                    : msgMetadata.exec_result;
                execResult?.output_files?.forEach((file) =>
                  addArtifact(file, allFiles),
                );
              } catch (e) {
                // Ignore parse errors
              }
            }

            // 2b. generated_images (image generation outputs)
            if (msgMetadata?.generated_images) {
              const genImages = Array.isArray(msgMetadata.generated_images)
                ? msgMetadata.generated_images.flat()
                : [];
              genImages.forEach((img) => addArtifact(img, allImages));
            }

            // 2c. generated_videos (video generation outputs)
            if (msgMetadata?.generated_videos) {
              const genVideos = Array.isArray(msgMetadata.generated_videos)
                ? msgMetadata.generated_videos.flat()
                : [];
              genVideos.forEach((video) => addArtifact(video, allVideos));
            }

            // 2d. generated_models (3D model outputs)
            if (msgMetadata?.generated_models) {
              const genModels = Array.isArray(msgMetadata.generated_models)
                ? msgMetadata.generated_models.flat()
                : [];
              genModels.forEach((model) => addArtifact(model, allModels));
            }

            // 2e. browser_recordings (browser/desktop session GIFs)
            // Each session appends its GIF here; preserved across sessions
            // even if msg.files is replaced on SSE.
            if (msgMetadata?.browser_recordings) {
              const recordings = Array.isArray(msgMetadata.browser_recordings)
                ? msgMetadata.browser_recordings.flat()
                : [];
              recordings.forEach((rec) => addArtifact(rec, allVideos));
            }
          });

          const hasArtifacts =
            allImages.length > 0 ||
            allFiles.length > 0 ||
            allAudios.length > 0 ||
            allVideos.length > 0 ||
            allModels.length > 0;

          const totalArtifacts =
            allImages.length +
            allFiles.length +
            allAudios.length +
            allVideos.length +
            allModels.length;

          if (!hasArtifacts) return null;

          return (
            <View style={{ marginTop: 15 }}>
              <AppText
                fontWeight="bold"
                style={{
                  fontSize: currentTheme?.theme?.small_font_size,
                  color: currentTheme?.theme?.font_color,
                  marginBottom: 8,
                }}
              >
                {t("planner.steps.artifacts", { count: totalArtifacts })}
              </AppText>
              {allImages.map((img, index) => (
                <ArtifactDownloadLink
                  key={`img-${index}`}
                  fileKey={img}
                  label={t("planner.steps.artifactImage")}
                  currentTheme={currentTheme}
                  S3downloadUrlRequest={S3downloadUrlRequest}
                  workspaceid={workspaceid}
                />
              ))}
              {allFiles.map((file, index) => (
                <ArtifactDownloadLink
                  key={`file-${index}`}
                  fileKey={file}
                  label={t("planner.steps.artifactFile")}
                  currentTheme={currentTheme}
                  S3downloadUrlRequest={S3downloadUrlRequest}
                  workspaceid={workspaceid}
                />
              ))}
              {allAudios.map((audio, index) => (
                <ArtifactDownloadLink
                  key={`audio-${index}`}
                  fileKey={audio}
                  label={t("planner.steps.artifactAudio")}
                  currentTheme={currentTheme}
                  S3downloadUrlRequest={S3downloadUrlRequest}
                  workspaceid={workspaceid}
                />
              ))}
              {allVideos.map((video, index) => (
                <ArtifactDownloadLink
                  key={`video-${index}`}
                  fileKey={video}
                  label={t("planner.steps.artifactVideo")}
                  currentTheme={currentTheme}
                  S3downloadUrlRequest={S3downloadUrlRequest}
                  workspaceid={workspaceid}
                />
              ))}
              {allModels.map((model, index) => (
                <ArtifactDownloadLink
                  key={`model-${index}`}
                  fileKey={model}
                  label={t("planner.steps.artifact3dModel")}
                  currentTheme={currentTheme}
                  S3downloadUrlRequest={S3downloadUrlRequest}
                  workspaceid={workspaceid}
                />
              ))}
            </View>
          );
        })()}
      </ScrollView>
    </View>
  );
};

// Helper to extract filename from path
const getFileName = (filePath) => {
  if (!filePath) return "";
  const parts = filePath.split("/");
  return parts[parts.length - 1] || filePath;
};

// Artifact download link component
const ArtifactDownloadLink = ({
  fileKey,
  label,
  currentTheme,
  S3downloadUrlRequest,
  workspaceid,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    console.log("[ArtifactDownloadLink] handleDownload called", {
      fileKey,
      workspaceid,
      hasS3downloadUrlRequest: !!S3downloadUrlRequest,
    });

    if (!S3downloadUrlRequest) {
      console.warn(
        "[ArtifactDownloadLink] S3downloadUrlRequest is not provided",
      );
      return;
    }
    if (!fileKey) {
      console.warn("[ArtifactDownloadLink] fileKey is not provided");
      return;
    }

    setIsDownloading(true);
    try {
      console.log("[ArtifactDownloadLink] Calling S3downloadUrlRequest...");
      const _url = await S3downloadUrlRequest({
        filename: fileKey,
        context: "pdf",
        workspaceid,
      });
      console.log("[ArtifactDownloadLink] response:", _url);

      if (_url?.url) {
        // Open in new tab or trigger download
        console.log("[ArtifactDownloadLink] Opening URL:", _url.url);
        if (isElectronRunning()) {
          window.electronAPI.downloadFileDl(_url.url, "_blank");
        } else {
          window.open(_url.url, "_blank");
        }
      } else {
        console.warn("[ArtifactDownloadLink] No url in response");
      }
    } catch (error) {
      console.error(
        "[ArtifactDownloadLink] Failed to get download URL:",
        error,
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const fileName = getFileName(fileKey);

  return (
    <AppText
      // leftIcon={AppAssets?.icons?.download || AppAssets?.icons?.goTo}
      iconSize={12}
      iconColor={currentTheme?.theme?.primary}
      isClickable
      onPress={handleDownload}
      disabled={isDownloading}
      style={{
        fontSize: currentTheme?.theme?.small_font_size,
        color: currentTheme?.theme?.primary,
        textDecorationLine: "underline",
        opacity: isDownloading ? 0.6 : 1,
      }}
      wrapperStyle={{
        marginBottom: 6,
      }}
      preventTranslation
    >
      {label}: {fileName}
    </AppText>
  );
};

// Collapsible section component
const CollapsibleSection = ({
  title,
  isExpanded,
  onToggle,
  currentTheme,
  children,
}) => (
  <View style={{ marginBottom: 12 }}>
    <Pressable
      onPress={onToggle}
      style={({ hovered }) => ({
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: 6,
        paddingHorizontal: 8,
        backgroundColor: hovered
          ? currentTheme?.theme?.grey
          : currentTheme?.theme?.softBackgroundColor,
        borderRadius: 6,
      })}
    >
      <AppText
        fontWeight="500"
        style={{
          fontSize: currentTheme?.theme?.extra_small_font_size,
          color: currentTheme?.theme?.font_color,
        }}
      >
        {title}
      </AppText>
      <AppIcon
        icon={AppAssets?.icons?.chevronRight || AppAssets?.icons?.goTo}
        size={10}
        color={currentTheme?.theme?.darkGrey}
        wrapperStyle={{
          transform: [{ rotate: isExpanded ? "90deg" : "0deg" }],
        }}
      />
    </Pressable>
    {isExpanded && <View style={{ marginTop: 6 }}>{children}</View>}
  </View>
);

export default StepDetailPanel;
